import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "reactstrap";
import StandardTable from "../../../ui/components/StandardTable";
import Pagination from "../../../components/utils/Pagination";
import EmptyState from "../../../components/EmptyState";
import { InlineLoader } from "../../../components/loader/CharacterizationLoader";
import exporterIcon from "../../../static/assets/Exporter.svg";
import CampaignHistoryFilterModal from "./CampaignHistoryFilterModal";
import CampaignNotificationHistoryRow from "./CampaignNotificationHistoryRow";
import { useCampaignNotificationHistory } from "../hooks/useCampaignNotificationHistory";
import {
  enrichHistoryRowDisplay,
  formatCampaignNotificationDateTime,
} from "../utils/campaignNotificationHistoryUtils";
import { downloadFile } from "../../../shared/utils/utils";
import { formatCampaignTitleCase } from "../campaignFormatUtils";
import { formatMaterialNameForDisplay } from "../../../shared/utils/materialNameNormalizer";

const TableHeaderCell = ({ label, columnKey, sortConfig, onSort }) => {
  const isActive = sortConfig.column === columnKey;
  const directionIcon = isActive
    ? sortConfig.direction === "asc"
      ? "fa-sort-up"
      : "fa-sort-down"
    : "fa-sort";

  return (
    <div
      className="table-standard-header-cell"
      style={{ cursor: "pointer" }}
      onClick={() => onSort(columnKey)}
      onKeyDown={(event) => event.key === "Enter" && onSort(columnKey)}
      role="button"
      tabIndex={0}
    >
      {label}
      <span className="sort-icon">
        <i className={`fas ${directionIcon}`} aria-hidden />
      </span>
    </div>
  );
};

const getPeriodOptions = (t) => [
  { label: t("campaign.historyFilterLast6Days"), value: "6" },
  { label: t("campaign.historyFilterLast7Days"), value: "7" },
  { label: t("campaign.historyFilterLast30Days"), value: "30" },
  { label: t("campaign.historyFilterWholeCampaign"), value: "all" },
];

const getTreatmentFilterOptions = (t) => [
  { label: t("All"), value: "all" },
  { label: t("campaign.historyFilterTreatmentTreated"), value: "treated" },
  {
    label: t("campaign.historyFilterTreatmentNotTreated"),
    value: "not_treated",
  },
];

const getRemovalFilterOptions = (t) => [
  { label: t("All"), value: "all" },
  { label: t("campaign.historyFilterRemovalRemoved"), value: "removed" },
  { label: t("campaign.historyFilterRemovalNotRemoved"), value: "not_removed" },
];

const CampaignNotificationHistoryTab = ({ campaignPeriod }) => {
  const { t } = useTranslation();
  const [periodDays, setPeriodDays] = useState("30");
  const [positioningFilter, setPositioningFilter] = useState("all");
  const [materialFilter, setMaterialFilter] = useState("all");
  const [treatmentFilter, setTreatmentFilter] = useState("all");
  const [removalFilter, setRemovalFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [recordPerPage, setRecordPerPage] = useState(10);
  const [resultPage, setResultPage] = useState(0);
  const [sortConfig, setSortConfig] = useState({
    column: "notification",
    direction: "desc",
  });
  const [isPeriodModalOpen, setIsPeriodModalOpen] = useState(false);
  const [isPositioningModalOpen, setIsPositioningModalOpen] = useState(false);
  const [isMaterialModalOpen, setIsMaterialModalOpen] = useState(false);
  const [isTreatmentModalOpen, setIsTreatmentModalOpen] = useState(false);
  const [isRemovalModalOpen, setIsRemovalModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const { rows, total, positionings, materials, isLoading, fetchHistory } =
    useCampaignNotificationHistory(campaignPeriod?._id, {
      periodDays,
      positioning: positioningFilter,
      material: materialFilter,
      treatment: treatmentFilter,
      removal: removalFilter,
      page: currentPage,
      pageSize: recordPerPage,
      sortColumn: sortConfig.column,
      sortDirection: sortConfig.direction,
    });

  const periodLabel =
    getPeriodOptions(t).find((option) => option.value === periodDays)?.label ??
    t("campaign.historyFilterWholeCampaign");

  const positioningLabel =
    positioningFilter === "all"
      ? t("All")
      : formatCampaignTitleCase(positioningFilter);

  const materialOptions = useMemo(() => {
    const tracked = campaignPeriod?.trackedMaterials || [];
    const merged = new Set([...tracked, ...materials]);
    return [
      { label: t("All"), value: "all" },
      ...Array.from(merged).map((material) => ({
        label: formatMaterialNameForDisplay(material, t),
        value: material,
      })),
    ];
  }, [campaignPeriod?.trackedMaterials, materials, t]);

  const materialLabel =
    materialOptions.find((option) => option.value === materialFilter)?.label ??
    t("All");

  const treatmentOptions = useMemo(() => getTreatmentFilterOptions(t), [t]);

  const treatmentLabel =
    treatmentOptions.find((option) => option.value === treatmentFilter)
      ?.label ?? t("All");

  const removalOptions = useMemo(() => getRemovalFilterOptions(t), [t]);

  const removalLabel =
    removalOptions.find((option) => option.value === removalFilter)?.label ??
    t("All");

  const positioningOptions = useMemo(
    () => [
      { label: t("All"), value: "all" },
      ...positionings
        .sort((a, b) => a.localeCompare(b))
        .map((positioning) => ({
          label: formatCampaignTitleCase(positioning),
          value: positioning,
        })),
    ],
    [positionings, t],
  );

  const displayRows = useMemo(
    () => rows.map((row) => enrichHistoryRowDisplay(row, t)),
    [rows, t],
  );
  const showListEmpty = !isLoading && displayRows.length === 0;

  useEffect(() => {
    setCurrentPage(1);
  }, [
    periodDays,
    positioningFilter,
    materialFilter,
    treatmentFilter,
    removalFilter,
    recordPerPage,
    sortConfig,
  ]);

  const handleSort = (columnKey) => {
    setSortConfig((current) => ({
      column: columnKey,
      direction:
        current.column === columnKey && current.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const data = await fetchHistory({
        export: "true",
        skipStateUpdate: true,
      });
      const exportRows = (data?.rows ?? []).map((row) =>
        enrichHistoryRowDisplay(row, t),
      );

      if (!exportRows.length) {
        return;
      }

      const headers = [
        t("campaign.historyColumnAlert"),
        t("campaign.historyColumnLocation"),
        t("campaign.historyFilterAlertTypeTitle"),
        t("campaign.historyColumnErrorCount"),
        t("campaign.historyColumnRemoval"),
      ];

      const csvRows = exportRows.map((row) => [
        row.treatmentDurationDisplay
          ? `${formatCampaignNotificationDateTime(row.date)}\n${row.treatmentDurationDisplay}`
          : formatCampaignNotificationDateTime(row.date),
        [row.positioning, row.wasteName]
          .filter(Boolean)
          .map((value) => formatCampaignTitleCase(value))
          .join(" / "),
        row.errorType ? formatMaterialNameForDisplay(row.errorType, t) : "-",
        row.errorCount ?? 0,
        row.removalDisplay?.sub
          ? `${row.removalDisplay.main} - ${row.removalDisplay.sub}`
          : row.removalDisplay?.main || "-",
      ]);

      const csvContent = [headers, ...csvRows]
        .map((row) =>
          row
            .map((cell) => {
              const value = cell ?? "";
              if (
                typeof value === "string" &&
                (value.includes(";") || value.includes("\n"))
              ) {
                return `"${value}"`;
              }
              return value;
            })
            .join(";"),
        )
        .join("\n");

      const blob = new Blob(["\uFEFF" + csvContent], {
        type: "text/csv;charset=utf-8;",
      });
      const fileName = `${campaignPeriod?.addressName || "campaign"}_${t("campaign.notificationHistoryTab")}.csv`;
      await downloadFile(blob, fileName);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="w-full">
      <Card className="table border-0 shadow-none bg-white rounded-lg">
        <StandardTable
          fluidColCount={showListEmpty ? 1 : 5}
          fixedColWidth={0}
          containerClassName={[
            "campaign-notification-history-table",
            showListEmpty ? "table-standard-container--list-empty" : null,
          ]
            .filter(Boolean)
            .join(" ")}
          columnAlignments={
            showListEmpty ? ["left"] : ["left", "left", "left", "left", "left"]
          }
          topBar={
            <section className="table-standard-top-bar campaign-notification-history-top-bar">
              <div className="table-standard-top-bar-inner">
                <div className="flex flex-wrap gap-2 items-center">
                  <button
                    type="button"
                    className="search-input table-standard-filter-button"
                    onClick={() => setIsPeriodModalOpen(true)}
                  >
                    <span>
                      {t("campaign.historyFilterAlertTitle")} :{" "}
                      <span className="font-semibold">{periodLabel}</span>
                    </span>
                    <i
                      className="fas fa-chevron-down table-standard-filter-icon"
                      aria-hidden
                    />
                  </button>
                  <button
                    type="button"
                    className="search-input table-standard-filter-button"
                    onClick={() => setIsPositioningModalOpen(true)}
                  >
                    <span>
                      {t("Positioning")} :{" "}
                      <span className="font-semibold">{positioningLabel}</span>
                    </span>
                    <i
                      className="fas fa-chevron-down table-standard-filter-icon"
                      aria-hidden
                    />
                  </button>
                  <button
                    type="button"
                    className="search-input table-standard-filter-button"
                    onClick={() => setIsMaterialModalOpen(true)}
                  >
                    <span>
                      {t("campaign.historyFilterAlertTypeTitle")} :{" "}
                      <span className="font-semibold">{materialLabel}</span>
                    </span>
                    <i
                      className="fas fa-chevron-down table-standard-filter-icon"
                      aria-hidden
                    />
                  </button>
                  <button
                    type="button"
                    className="search-input table-standard-filter-button"
                    onClick={() => setIsTreatmentModalOpen(true)}
                  >
                    <span>
                      {t("campaign.historyFilterTreatmentTitle")} :{" "}
                      <span className="font-semibold">{treatmentLabel}</span>
                    </span>
                    <i
                      className="fas fa-chevron-down table-standard-filter-icon"
                      aria-hidden
                    />
                  </button>
                  <button
                    type="button"
                    className="search-input table-standard-filter-button"
                    onClick={() => setIsRemovalModalOpen(true)}
                  >
                    <span>
                      {t("campaign.historyColumnRemoval")} :{" "}
                      <span className="font-semibold">{removalLabel}</span>
                    </span>
                    <i
                      className="fas fa-chevron-down table-standard-filter-icon"
                      aria-hidden
                    />
                  </button>
                </div>
                <button
                  type="button"
                  className="char-button char-button-primary campaign-notification-history-export"
                  onClick={handleExport}
                  disabled={isLoading || isExporting || total === 0}
                >
                  <img src={exporterIcon} alt="" aria-hidden />
                  {t("Export")}
                </button>
              </div>
            </section>
          }
          header={
            showListEmpty ? null : (
              <div className="table-standard-header">
                <TableHeaderCell
                  label={t("campaign.historyColumnAlert")}
                  columnKey="notification"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
                <TableHeaderCell
                  label={t("campaign.historyColumnLocation")}
                  columnKey="positioning"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
                <TableHeaderCell
                  label={t("campaign.historyFilterAlertTypeTitle")}
                  columnKey="errorType"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
                <TableHeaderCell
                  label={t("campaign.historyColumnErrorCount")}
                  columnKey="numbers"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
                <TableHeaderCell
                  label={t("campaign.historyColumnRemoval")}
                  columnKey="removal"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
              </div>
            )
          }
          bottom={
            total > 0 ? (
              <div className="table-standard-bottom table-pagination-bottom">
                <Pagination
                  totalEntry={total}
                  recordPerPage={recordPerPage}
                  setRecordPerPage={setRecordPerPage}
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  resultPage={resultPage}
                  setResultPage={setResultPage}
                />
              </div>
            ) : null
          }
        >
          {isLoading ? (
            <div className="table-standard-row">
              <div
                className="table-standard-row-cell justify-center min-h-[200px]"
                style={{ gridColumn: "1 / -1" }}
              >
                <InlineLoader height="200px" />
              </div>
            </div>
          ) : displayRows.length > 0 ? (
            displayRows.map((row) => (
              <CampaignNotificationHistoryRow key={row.rowId} row={row} />
            ))
          ) : (
            <EmptyState title={t("No data to display")} />
          )}
        </StandardTable>
      </Card>

      <CampaignHistoryFilterModal
        isOpen={isPeriodModalOpen}
        onClose={() => setIsPeriodModalOpen(false)}
        currentValue={periodDays}
        title={t("campaign.historyFilterAlertTitle")}
        options={getPeriodOptions(t)}
        onApply={(value) => {
          setPeriodDays(value);
          setCurrentPage(1);
        }}
      />
      <CampaignHistoryFilterModal
        isOpen={isPositioningModalOpen}
        onClose={() => setIsPositioningModalOpen(false)}
        currentValue={positioningFilter}
        title={t("Positioning")}
        options={positioningOptions}
        onApply={(value) => {
          setPositioningFilter(value);
          setCurrentPage(1);
        }}
      />
      <CampaignHistoryFilterModal
        isOpen={isMaterialModalOpen}
        onClose={() => setIsMaterialModalOpen(false)}
        currentValue={materialFilter}
        title={t("campaign.historyFilterAlertTypeTitle")}
        options={materialOptions}
        onApply={(value) => {
          setMaterialFilter(value);
          setCurrentPage(1);
        }}
      />
      <CampaignHistoryFilterModal
        isOpen={isTreatmentModalOpen}
        onClose={() => setIsTreatmentModalOpen(false)}
        currentValue={treatmentFilter}
        title={t("campaign.historyFilterTreatmentTitle")}
        options={treatmentOptions}
        onApply={(value) => {
          setTreatmentFilter(value);
          setCurrentPage(1);
        }}
      />
      <CampaignHistoryFilterModal
        isOpen={isRemovalModalOpen}
        onClose={() => setIsRemovalModalOpen(false)}
        currentValue={removalFilter}
        title={t("campaign.historyColumnRemoval")}
        options={removalOptions}
        onApply={(value) => {
          setRemovalFilter(value);
          setCurrentPage(1);
        }}
      />
    </div>
  );
};

export default CampaignNotificationHistoryTab;
